import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Eye, EyeSlash, GoogleLogo, Check } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
  'Sole Proprietorship', 'Partnership', 'Private Limited Company',
  'Public Limited Company', 'LLP', 'One Person Company (OPC)',
  'Hindu Undivided Family (HUF)', 'Trust', 'Other'
];

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
    business_name: '', business_type: '',
    selected_categories: []
  });
  const [categories, setCategories] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/api/categories`).then(res => {
      setCategories(res.data.filter(c => c.level === 1));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleCategory = (catId) => {
    setFormData(prev => ({
      ...prev,
      selected_categories: prev.selected_categories.includes(catId)
        ? prev.selected_categories.filter(id => id !== catId)
        : [...prev.selected_categories, catId]
    }));
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.email || !formData.password) { setError('Please fill all required fields'); return false; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return false; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return false; }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        business_name: formData.business_name || null,
        business_type: formData.business_type || null,
        selected_categories: formData.selected_categories.length > 0 ? formData.selected_categories : null,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-section">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-6">
            <img src="https://customer-assets.emergentagent.com/job_merchant-portal-112/artifacts/dsd8687t_Vinculum-logo-new.svg" alt="Vinculum" className="h-9" data-testid="register-logo" />
            <p className="text-sm text-[#5a5c87] mt-2">Marketplace Seller Management</p>
          </div>

          <div className="space-y-5">
            <div>
              <h2 className="font-bold text-2xl text-[#0A0A0A]">Create your account</h2>
              <p className="text-sm text-[#5a5c87] mt-1">
                {step === 1 ? 'Personal & business details' : 'Select your selling categories'}
              </p>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2">
              {[1, 2].map(s => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= s ? 'bg-[#7d64ff] text-white' : 'bg-[#f0f0f0] text-[#9c9db7]'}`}>
                    {step > s ? <Check size={14} weight="bold" /> : s}
                  </div>
                  {s < 2 && <div className={`w-12 h-0.5 ${step > 1 ? 'bg-[#7d64ff]' : 'bg-[#e5e5ea]'}`} />}
                </div>
              ))}
            </div>

            {error && (
              <div className="p-3 bg-[#ea4b5a]/10 border border-[#ea4b5a] text-[#c43447] text-sm rounded" data-testid="register-error">{error}</div>
            )}

            {step === 1 && (
              <form onSubmit={e => { e.preventDefault(); if (validateStep1()) setStep(2); }} className="space-y-3">
                <div>
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Full Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required className="mt-1 rounded-sm border-[#e5e5ea]" data-testid="register-name" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="seller@company.com" required className="mt-1 rounded-sm border-[#e5e5ea]" data-testid="register-email" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Phone</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+91 9876543210" className="mt-1 rounded-sm border-[#e5e5ea]" data-testid="register-phone" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="business_name" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Business Name</Label>
                    <Input id="business_name" name="business_name" value={formData.business_name} onChange={handleChange} placeholder="My Store Pvt Ltd" className="mt-1 rounded-sm border-[#e5e5ea]" data-testid="register-business-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Business Type</Label>
                    <Select value={formData.business_type || "none"} onValueChange={v => setFormData({ ...formData, business_type: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1 rounded-sm border-[#e5e5ea]" data-testid="register-business-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select type</SelectItem>
                        {BUSINESS_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Password *</Label>
                  <div className="relative mt-1">
                    <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} placeholder="Min. 6 characters" required className="rounded-sm border-[#e5e5ea] pr-10" data-testid="register-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a5c87]">
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Confirm Password *</Label>
                  <Input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} placeholder="Repeat password" required className="mt-1 rounded-sm border-[#e5e5ea]" data-testid="register-confirm-password" />
                </div>
                <Button type="submit" className="w-full bg-[#7d64ff] hover:bg-[#6153a7] text-white font-semibold py-2.5 rounded-sm" data-testid="register-next-btn">
                  Next: Select Categories
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-3">Select categories you want to sell in</p>
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map(cat => (
                      <button
                        key={cat.category_id}
                        type="button"
                        onClick={() => toggleCategory(cat.category_id)}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm font-medium transition-all ${
                          formData.selected_categories.includes(cat.category_id)
                            ? 'border-[#7d64ff] bg-[#7d64ff]/5 text-[#7d64ff]'
                            : 'border-[#e5e5ea] text-[#3c3c64] hover:border-[#d1d1d6]'
                        }`}
                        data-testid={`cat-select-${cat.slug}`}
                      >
                        {formData.selected_categories.includes(cat.category_id) && (
                          <Check size={16} weight="bold" className="text-[#7d64ff] flex-shrink-0" />
                        )}
                        <span className="truncate">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                  {formData.selected_categories.length > 0 && (
                    <p className="text-xs text-[#7d64ff] mt-2 font-medium">{formData.selected_categories.length} categor{formData.selected_categories.length === 1 ? 'y' : 'ies'} selected</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white font-semibold py-2.5 rounded-sm" data-testid="register-submit">
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </div>
              </form>
            )}

            {step === 1 && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#e5e5ea]"></div></div>
                  <div className="relative flex justify-center text-xs"><span className="px-4 bg-white text-[#5a5c87] uppercase tracking-wider">Or continue with</span></div>
                </div>
                <Button type="button" variant="outline" onClick={googleLogin} className="w-full border-[#e5e5ea] hover:bg-[#fcfcfc] font-medium py-2.5 rounded-sm" data-testid="google-register">
                  <GoogleLogo size={20} weight="bold" className="mr-2" /> Sign up with Google
                </Button>
              </>
            )}

            <p className="text-center text-sm text-[#5a5c87]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#7d64ff] font-semibold hover:underline" data-testid="login-link">Sign in</Link>
            </p>
          </div>
          <p className="text-[11px] text-[#9c9db7] text-center mt-8 tracking-wide" data-testid="auth-footer">
            Powered by <span className="font-semibold text-[#464C82]">Vinculum</span> &middot; &copy; {new Date().getFullYear()} Vinculum Group. All rights reserved.
          </p>
        </div>
      </div>

      <div className="auth-media-section" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/9cac01b7-677d-4f97-989d-99ea54579b96/images/fbffb3aa9fdd9bb10aaaf38d06c9b83f6304c63cb50ab93ebba43250f2df1c4c.png')` }}>
        <div className="h-full w-full bg-gradient-to-br from-[#7d64ff]/30 to-transparent flex items-end p-12">
          <div className="text-white">
            <h2 className="font-black text-4xl mb-4">Grow Your<br />E-commerce Empire</h2>
            <p className="text-lg opacity-90 max-w-md">Join thousands of sellers managing multi-channel sales with ease.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
